// ============================================================
// WorkspaceDocumentPane — the central work/document canvas.
//
// A framed document surface (max ~680px, QUAM reference), with
// progressive-build affordances: a blinking caret while
// `building`, agent-attributed sections, and an honest empty
// state with a next action when there is nothing yet.
//
//   <WorkspaceDocumentPane accent={cyan} title="…" meta="…">
//     <WorkspaceDocSection heading="1. Findings" agent={{ name, accent }}>
//       …
//     </WorkspaceDocSection>
//   </WorkspaceDocumentPane>
// ============================================================

import {
  tint,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
} from './workspaceTheme'

export function WorkspaceDocSection({
  heading,
  agent,               // { name, accent } — real attribution only
  kind = 'body',       // body | callout | proof
  accent,
  children,
  style: styleOverride,
}) {
  const attributionAccent = agent?.accent || accent || WORKSPACE_ACCENTS.cyan

  const base = {
    marginBottom: 14,
    animation: 'sc-fade-in var(--sc-dur-med, 350ms) var(--sc-ease, ease) both',
    ...styleOverride,
  }

  const inner = (
    <>
      {heading && (
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: WORKSPACE_TEXT.primary,
            fontFamily: WORKSPACE_FONT.ui,
            marginBottom: 5,
          }}
        >
          {heading}
        </div>
      )}
      {agent && (
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: attributionAccent,
            fontFamily: WORKSPACE_FONT.mono,
            marginBottom: 3,
          }}
        >
          {agent.name}
        </div>
      )}
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.8,
          color: WORKSPACE_TEXT.secondary,
          fontFamily: WORKSPACE_FONT.ui,
        }}
      >
        {children}
      </div>
    </>
  )

  if (kind === 'callout') {
    return (
      <div
        style={{
          ...base,
          padding: '12px 16px',
          borderRadius: 8,
          background: tint(attributionAccent, 7),
          borderLeft: `3px solid ${attributionAccent}`,
        }}
      >
        {inner}
      </div>
    )
  }

  if (kind === 'proof') {
    return (
      <div
        style={{
          ...base,
          padding: '12px 16px',
          borderRadius: 8,
          background: tint(WORKSPACE_ACCENTS.purple, 6),
          border: `1px solid ${tint(WORKSPACE_ACCENTS.purple, 14)}`,
        }}
      >
        {inner}
      </div>
    )
  }

  return (
    <div
      style={{
        ...base,
        ...(agent
          ? { borderLeft: `2px solid ${tint(attributionAccent, 30)}`, paddingLeft: 12 }
          : null),
      }}
    >
      {inner}
    </div>
  )
}

export default function WorkspaceDocumentPane({
  accent = WORKSPACE_ACCENTS.cyan,
  title,
  meta,
  building = false,
  buildingLabel = 'Building…',
  empty,               // { glyph?, title, body, action? } — honest empty state
  maxWidth = 680,
  footer,
  children,
  style: styleOverride,
}) {
  const hasContent = Boolean(title) || Boolean(children)

  if (!hasContent && empty) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 260,
          textAlign: 'center',
          padding: 24,
          ...styleOverride,
        }}
      >
        {empty.glyph && (
          <div aria-hidden style={{ fontSize: 30, color: tint(accent, 25), marginBottom: 12 }}>
            {empty.glyph}
          </div>
        )}
        <div style={{ fontSize: 15, fontWeight: 600, color: WORKSPACE_TEXT.secondary, fontFamily: WORKSPACE_FONT.ui }}>
          {empty.title}
        </div>
        <div
          style={{
            marginTop: 5,
            fontSize: 12,
            lineHeight: 1.7,
            maxWidth: 380,
            color: WORKSPACE_TEXT.muted,
            fontFamily: WORKSPACE_FONT.ui,
          }}
        >
          {empty.body}
        </div>
        {empty.action && <div style={{ marginTop: 16 }}>{empty.action}</div>}
      </div>
    )
  }

  return (
    <div style={{ maxWidth, margin: '0 auto', minWidth: 0, ...styleOverride }}>
      <div
        style={{
          background: 'rgba(255,255,255,0.015)',
          border: `1px solid ${tint(accent, 10)}`,
          borderRadius: 14,
          padding: '26px 30px',
          boxShadow: `0 4px 40px rgba(0,0,0,0.3), inset 0 1px 0 ${tint(accent, 8)}`,
        }}
      >
        {title && (
          <div
            style={{
              fontSize: 21,
              fontWeight: 800,
              letterSpacing: '-0.01em',
              color: WORKSPACE_TEXT.primary,
              fontFamily: WORKSPACE_FONT.ui,
            }}
          >
            {title}
          </div>
        )}
        {meta && (
          <div
            style={{
              marginTop: 3,
              marginBottom: 12,
              fontSize: 10,
              color: WORKSPACE_TEXT.muted,
              fontFamily: WORKSPACE_FONT.mono,
              letterSpacing: '0.05em',
            }}
          >
            {meta}
          </div>
        )}
        {(title || meta) && (
          <div aria-hidden style={{ height: 1, background: WORKSPACE_SURFACE.line, marginBottom: 16 }} />
        )}

        {children}

        {building && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
            <span
              aria-hidden
              style={{
                width: 2,
                height: 14,
                background: accent,
                borderRadius: 1,
                animation: 'sc-blink 1s step-end infinite',
              }}
            />
            <span
              style={{
                fontSize: 10,
                fontStyle: 'italic',
                color: WORKSPACE_TEXT.muted,
                fontFamily: WORKSPACE_FONT.ui,
              }}
            >
              {buildingLabel}
            </span>
          </div>
        )}

        {footer}
      </div>
    </div>
  )
}
